// The HubSpot form is built in the new editor, which only renders as an iframe
// and so can't follow the site's light/dark theme. Instead we read the form's
// field definitions from HubSpot's public render-definition endpoint (no auth)
// and render them as native, themed fields. Editing the form in HubSpot is
// reflected automatically — the fields are fetched server-side on each request.

const REBELLION_FORM = {
  region: "na2",
  portalId: "243539859",
  formId: "1c9667b1-a95f-4eaa-8450-7ae835a369f9",
} as const

export const SUBMIT_URL = `https://api.hsforms.com/submissions/v3/integration/submit/${REBELLION_FORM.portalId}/${REBELLION_FORM.formId}`

const RENDER_DEFINITION_URL = `https://forms-${REBELLION_FORM.region}.hsforms.net/embed/v4/render-definition/${REBELLION_FORM.portalId}/${REBELLION_FORM.formId}`

type RebellionFieldType = "text" | "email" | "checkbox"

export interface RebellionField {
  /** HubSpot property name used when submitting, e.g. "email", "firstname". */
  name: string
  label: string
  type: RebellionFieldType
  required: boolean
}

// If HubSpot is unreachable we still render a working email-only form rather
// than break the page (email is the form's only required field).
export const FALLBACK_FIELDS: RebellionField[] = [
  { name: "email", label: "Email", type: "email", required: true },
]

// HubSpot module types we render. Everything else (submitButton, richText,
// layout rows/steps) is structural and handled separately or ignored.
const SUPPORTED_TYPES: Record<string, RebellionFieldType> = {
  text: "text",
  email: "email",
  singleCheckbox: "checkbox",
}

function stripHtml(value: string): string {
  return value
    .replace(/<[^>]*>/g, "")
    .replace(/\s+/g, " ")
    .trim()
}

/** Flatten HubSpot's nested step/row module tree into the input fields. */
export function parseRenderDefinition(definition: unknown): RebellionField[] {
  const root = (definition as { form?: { modules?: unknown[] } })?.form?.modules
  if (!Array.isArray(root)) return []

  const fields: RebellionField[] = []
  const visit = (modules: unknown[]) => {
    for (const mod of modules) {
      if (typeof mod !== "object" || mod === null) continue
      const m = mod as {
        type?: string
        modules?: unknown[]
        label?: string
        required?: boolean
        propertyReference?: string
      }
      if (Array.isArray(m.modules)) {
        visit(m.modules)
        continue
      }
      const type = m.type ? SUPPORTED_TYPES[m.type] : undefined
      if (!type || typeof m.propertyReference !== "string") continue
      // propertyReference is "<objectTypeId>/<property>", e.g. "0-1/email".
      const name = m.propertyReference.split("/").at(-1) ?? m.propertyReference
      fields.push({
        name,
        label: stripHtml(m.label ?? name),
        type,
        required: m.required === true,
      })
    }
  }
  visit(root)
  return fields
}

export async function fetchRebellionFields(): Promise<RebellionField[]> {
  try {
    const response = await fetch(RENDER_DEFINITION_URL)
    if (!response.ok) return FALLBACK_FIELDS
    const fields = parseRenderDefinition(await response.json())
    return fields.length > 0 ? fields : FALLBACK_FIELDS
  } catch {
    return FALLBACK_FIELDS
  }
}
